import * as React from "react";
import * as ReactDom from "react-dom";

import l10n from "diy/util/l10n";
import globalEvents = require("diy/util/globalEvents");

import Api from "../Api";
import Notification from "./Notification";
import OverlayApi from "./NotificationCenterOverlay";
import ConfigFactory from "../utils/ConfigFactory";
import {NotificationConfig, NotificationState, NotificationStateList} from "../definitions/Config";

import {colors} from "material-ui/styles";
import mwsSpacings = require("diy/material-ui/spacings");
import Card from "material-ui/Card/Card";
import CardText from "material-ui/Card/CardText";
import FlatButton from "material-ui/FlatButton";

export interface NotificationCenterProps {

    /** Visibility of the Notification Center (yes / no) */
    open: boolean;

    /** Notification Configs that must be rendered in the list */
    notificationStateList: NotificationStateList;

    /** CSS Class */
    className?: string;
}

/**
 * Visual Component for the Notification Center
 * Reacts to the API notifications list
 */
export default class NotificationCenter extends React.Component<NotificationCenterProps, {}> {

    handleDismissAll = () => {
        Api.schedule(() => {
            Api.markAllAs(["deleted", "read", "dismissed"]);
        });
    };

    handleDocumentClick = (event: MouseEvent) => {
        let target = event.target as HTMLElement;
        let rootElement = ReactDom.findDOMNode(this) as HTMLElement;

        if (!rootElement.contains(target)) {
            Api.toggleCenter(false);
        }
    };

    adjustHeight = () => {
        let extraMargin = 120;
        let containerElement = ReactDom.findDOMNode(this.refs["container"]) as HTMLElement;

        if (containerElement) {
            let viewportHeight = document.body.clientHeight;
            let containerElementHeight = containerElement.offsetHeight;

            /** We let a margin of 60px to include the MWS Header + the NC Header */
            if (containerElementHeight > viewportHeight) {
                containerElement.style.maxHeight = (viewportHeight - extraMargin) + "px";
            }
        }
    };

    getStyles() {
        let smallSpacing = mwsSpacings.small + "px";
        let mediumSpacing = mwsSpacings.medium + "px";

        return {
            root: {
                top: "40px",
                left: smallSpacing,
                width: "432px",
                paddingTop: "14px",
                position: "fixed",
                pointerEvents: "all",
                zIndex: diy.editor.getNewTopZIndex(),
                display: this.props.open ? "block" : "none"
            },
            wrapper: {
                overflow: "hidden",
                position: "relative",
                backgroundColor: "#eceef1",
                borderTopLeftRadius: "0px",
                borderTopRightRadius: "0px",
                borderTop: "3px solid " + colors.blue500
            },
            container: {
                maxHeight: "none",
                overflowY: "auto",
                overflowX: "hidden"
            },
            arrow: {
                top: "0",
                left: "15px",
                display: "block",
                position: "absolute",
                color: colors.blue500,
                textShadow: "0 1px 4px rgba(0, 0, 0, 0.24)"
            },
            message: {
                padding: mediumSpacing,
                backgroundColor: colors.white,
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.24)"
            },
            header: {
                height: "auto",
                minHeight: "36px",
                padding: smallSpacing + " " + smallSpacing + " " + smallSpacing + " " + mediumSpacing,
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                backgroundColor: colors.white,
                boxShadow: "0 1px 4px rgba(0, 0, 0, 0.24)"
            } as React.CSSProperties,
            title: {
                fontWeight: 600
            }
        };
    }

    /**
     * Closes the Notification Center when the user clicks outside of it +
     * Adds an overlay to block the rest of the UI when it's opened
     */
    componentDidUpdate() {
        if (this.props.open) {
            OverlayApi.show();
            this.adjustHeight();
            document.addEventListener("click", this.handleDocumentClick);
        } else {
            OverlayApi.hide();
            document.removeEventListener("click", this.handleDocumentClick);
        }
    }

    /**
     * Renders the Notification Center header
     *
     * @param {boolean} permitDeleteAll
     * @param {Map<number, NotificationConfig>} notificationConfigList
     * @returns {any}
     */
    renderHeader(notificationConfigList: Map<number, NotificationConfig>, permitDeleteAll: boolean) {
        let styles = this.getStyles();

        return (
            <CardText style={styles.header}>
                <div style={styles.title}>
                    {l10n("notifications.header")}
                </div>

                {(permitDeleteAll && notificationConfigList.size > 1)
                    ? <FlatButton
                          label={l10n("notifications.deleteAll")}
                          onTouchTap={() => {
                              this.handleDismissAll();
                          }} />
                    : ""}
            </CardText>
        );
    }

    /**
     * Renders all non-deleted notifications to show
     *
     * @param {Map<number, NotificationConfig>} notificationConfigList
     */
    renderNotifications(notificationConfigList: Map<number, NotificationConfig>) {
        let styles = this.getStyles();
        let content: JSX.Element | JSX.Element[];

        /** If there is no Notification, an message is shown */
        if (notificationConfigList.size === 0) {
            content = this.renderEmptyMessage();
        } else {
            content =
                <div ref="container" style={styles.container}>
                    {Array.from(notificationConfigList)
                        .map(([key, notificationConfig]) => {
                            return (
                                <Notification
                                    key={key}
                                    config={notificationConfig} />
                            );
                        })}
                </div>;
        }
        return content;
    }

    /**
     * Renders the message when the Notification Center is empty
     */
    renderEmptyMessage() {
        let styles = this.getStyles();

        return (
            <CardText style={styles.message}>
                {l10n("notifications.zeroNotifications")}
            </CardText>
        );
    }

    /**
     * Renders the Notification Center
     */
    render() {
        let styles = this.getStyles();
        let permitDeleteAll = true;
        let notificationConfigList = new Map<number, NotificationConfig>();

        /** Computes a Map containing all notifications to show */
        this.props.notificationStateList
            .forEach((notificationState: NotificationState) => {
                try {
                    if (!notificationState.deleted && notificationState.dismissed) {
                        let notificationConfig = ConfigFactory.factory(
                            notificationState
                        );
                        if (!notificationConfig.cardOptions.hidden) {
                            /**
                             * Checks if there is a non-deletable Notification in the list,
                             * then it's forbidden to delete all Notifications at once
                             */
                            if (!notificationConfig.cardOptions.closable) {
                                permitDeleteAll = false;
                            }
                            notificationConfigList.set(
                                notificationConfig.type,
                                notificationConfig
                            );
                        }
                    }
                } catch (reason) {
                    globalEvents.trigger("error.diy-editor", {
                        message: "Could not render Notification: " + reason
                    });
                }
            });

        /** Notification Center is only rendered when opened */
        return (
            <div
                style={styles.root}
                className={this.props.className}
                data-test-id="notification-center">

                <div style={styles.arrow}>{String.fromCharCode(9650)}</div>
                <Card style={styles.wrapper}>
                    {this.renderHeader(notificationConfigList, permitDeleteAll)}
                    {this.renderNotifications(notificationConfigList)}
                </Card>
            </div>
        );
    }
}



/** WEBPACK FOOTER **
 ** ./build/js/~/tslint-loader!./source/cdn/document_root/s/diy/toolbar/notificationNG/components/NotificationCenter.tsx
 **/